<script lang="ts">
	import LoadingSpinner from '#lib/LoadingSpinner.svelte';
	import ImageLightbox from '#lib/ImageLightbox.svelte';
	import { t } from './i18n/tranlsator.svelte';
	import { getChatContext } from '#src/contexts/chat.context.svelte.ts';
	import { getModalContext } from '@astlai/common/contexts/modal.context.svelte.ts';
	import IconDownload from 'virtual:icons/material-symbols/download-rounded';
	import IconError from 'virtual:icons/material-symbols/error-outline-rounded';
	import IconFile from 'virtual:icons/material-symbols/draft-outline-rounded';
	import IconImage from 'virtual:icons/material-symbols/image-outline-rounded';

	const chatCtx = getChatContext();
	const modalCtx = getModalContext();

	const isEmpty = $derived(chatCtx.assets.length === 0);

	function kindLabel(kind: 'image' | 'file') {
		return kind === 'image' ? t('assets.kind.image') : t('assets.kind.file');
	}
</script>

<div class="body">
	{#if isEmpty}
		<div class="no-context-wrapper">
			<span class="no-context-message">{t('assets.placeholder')}</span>
		</div>
	{:else}
		<div class="assets">
			{#each chatCtx.assets as asset (asset.call_id ?? asset.assetId)}
				<div class="asset-card glass">
					<div class="thumb" class:has-error={asset.status === 'error'}>
						{#if asset.kind === 'image' && asset.status === 'ready' && asset.url}
							<button
								type="button"
								class="thumb-button"
								onclick={() =>
									modalCtx.openModal(
										ImageLightbox,
										{ src: asset.url, alt: asset.name || '' },
										{ appearance: 'fullscreen' }
									)}
							>
								<img src={asset.url} alt={asset.name || ''} loading="lazy" />
							</button>
						{:else if asset.status === 'generating'}
							<LoadingSpinner />
						{:else if asset.status === 'error'}
							<IconError />
						{:else if asset.kind === 'image'}
							<IconImage />
						{:else}
							<IconFile />
						{/if}
					</div>
					<div class="info">
						<span class="name">{kindLabel(asset.kind)}: {asset.name || t('assets.untitled')}</span>
						{#if asset.status === 'error' && asset.error}
							<span class="description error">{asset.error}</span>
						{:else if asset.description || asset.prompt}
							<span class="description">{asset.description || asset.prompt}</span>
						{/if}
					</div>
					<div class="actions">
						{#if asset.status === 'ready' && asset.url}
							<a
								href={asset.url}
								download={asset.name || undefined}
								target="_blank"
								rel="noopener"
								aria-label={t('assets.download')}
								class="button icon-button"
							>
								<IconDownload />
							</a>
						{/if}
					</div>
				</div>
			{/each}
		</div>
	{/if}
</div>

<style lang="scss">
	@use '@astlai/common/styles/mixins' as lib;

	.no-context-wrapper {
		@include lib.flex-column();
		@include lib.flex-align(center, center);
		flex: 1 1 auto;
		.no-context-message {
			color: var(--text-secondary);
			padding: 0 2rem;
			text-align: center;
		}
	}

	.body {
		padding: 0 1rem 1rem 1rem;
		@include lib.flex-column();
		flex: 1 1 auto;
		overflow: hidden;

		.assets {
			@include lib.flex-column();
			gap: 0.5rem;
			overflow-y: auto;

			.asset-card {
				flex: 0 0 auto;
				border-radius: 0.75rem;
				padding: 0.5rem;
				@include lib.flex-row();
				@include lib.flex-align(flex-start, center);
				gap: 0.75rem;
				border: 1px solid var(--border-subtle);

				.thumb {
					flex: 0 0 auto;
					width: 2.5rem;
					height: 2.5rem;
					border-radius: 0.25rem;
					overflow: hidden;
					background: var(--surface-2);
					@include lib.flex-row();
					@include lib.flex-align(center, center);
					font-size: 1.25rem;
					color: var(--text-secondary);

					&.has-error {
						background: var(--surface-error-subtle);
						color: var(--text-error);
					}

					.thumb-button {
						width: 100%;
						height: 100%;
						padding: 0;
						border: 0;
						background: none;
						cursor: pointer;
					}

					img {
						width: 100%;
						height: 100%;
						object-fit: cover;
					}
				}

				.info {
					flex: 1 1 auto;
					@include lib.flex-column();
					@include lib.flex-align(flex-start, stretch);
					gap: 0.125rem;
					overflow: hidden;
					min-width: 0;

					.name {
						font-size: 0.875rem;
						@include lib.ellipsis();
					}

					.description {
						font-size: 0.75rem;
						color: var(--text-secondary);
						@include lib.ellipsis();

						&.error {
							color: var(--text-error);
						}
					}
				}

				.actions {
					flex: 0 0 auto;
				}
			}
		}
	}
</style>
